// packages/web-ui/src/panels/chat-panel/useThinkingIndicator.ts
//
// W2-S5 AC #7 — Visibility logic for the "Thinking…" indicator.
//
// Pure timing hook — encapsulates:
//   • show when a user message is in flight AND no token has arrived yet
//   • hide on first token
//   • re-show 200 ms after a `chat:toolCall` or `chat:toolResult` if no
//     new token has arrived
//   • the 200 ms timer resets on every new token
//
// The hook is decoupled from zustand for testability — callers pass the
// four timing fields. The slice's `selectPendingAssistantMessageId(...)`
// + `lastTokenAtBySession[id]` + `lastToolCallAtBySession[id]` +
// `lastToolResultAtBySession[id]` feed straight in.

import { useEffect, useRef, useState } from 'react';

export interface ThinkingTimingInputs {
  pendingAssistantMessageId: string | null;
  /**
   * Wall-clock timestamp of the moment the in-flight assistant turn was
   * started (set by `sendMessage`'s optimistic update). Used to scope
   * the `lastTokenAt` comparison to the CURRENT turn — without this, a
   * stale `lastTokenAt` from a prior turn in the same session keeps
   * Phase A from re-firing on follow-up messages, leaving the indicator
   * hidden until a tool event arrives several seconds later.
   *
   * Pass `0` (or undefined) for first-turn / no-pending-turn states.
   */
  pendingStartedAt?: number;
  lastTokenAt: number | null;
  lastToolCallAt: number | null;
  lastToolResultAt: number | null;
  /** When non-null, force the indicator off (e.g. SSE explicitly disconnected). */
  forceOff?: boolean;
}

function maxOrNull(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return a > b ? a : b;
}

export interface ThinkingIndicatorOptions {
  /** ms threshold after `lastToolResultAt` before re-showing. Default 200. */
  toolResultGraceMs?: number;
  /** Override Date.now for tests. */
  now?: () => number;
}

/**
 * Returns whether the indicator should currently be visible.
 *
 * The hook re-runs on every input change AND after the grace timer fires —
 * so React re-renders the indicator on/off correctly with `vi.useFakeTimers`.
 */
export function useThinkingIndicatorVisible(
  inputs: ThinkingTimingInputs,
  opts: ThinkingIndicatorOptions = {},
): boolean {
  const grace = opts.toolResultGraceMs ?? 200;
  const now = opts.now ?? Date.now;

  // We schedule a re-render after the grace window so the indicator can
  // re-appear even if no further events fire.
  const [tick, setTick] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastToolEvent = maxOrNull(inputs.lastToolCallAt, inputs.lastToolResultAt);

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (lastToolEvent === null) return;
    const delay = grace - (now() - lastToolEvent);
    if (delay <= 0) return;
    timerRef.current = setTimeout(() => {
      setTick((t) => t + 1);
    }, delay);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [lastToolEvent, inputs.lastTokenAt, grace, now]);

  // Force-off short-circuit.
  if (inputs.forceOff === true) return false;

  // No pending response → indicator hidden.
  if (inputs.pendingAssistantMessageId === null) return false;

  const lastToken = inputs.lastTokenAt;
  const pendingStartedAt = inputs.pendingStartedAt ?? 0;

  // Phase A — no token for THIS turn yet → indicator visible.
  // Two sub-cases collapse to one:
  //   (a) lastToken === null    — no token has ever arrived for this session.
  //   (b) lastToken < pendingStartedAt — a token arrived, but for a
  //       previous turn (the leftover timestamp is older than the moment
  //       the current pending message was registered).
  // Both mean "the current turn hasn't produced a token yet" → show
  // indicator. Without sub-case (b) every follow-up turn waits behind
  // stale state until Phase B fires on the first tool event, which is
  // exactly what the user reported.
  if (lastToken === null || lastToken < pendingStartedAt) return true;

  // Phase B/C — a tool event (call OR result) arrived AFTER the most recent
  // token AND grace window has elapsed without a new token → indicator
  // visible again. The grace window suppresses on/off flicker when the next
  // token follows the tool event immediately.
  if (lastToolEvent !== null && lastToolEvent > lastToken) {
    const elapsed = now() - lastToolEvent;
    return elapsed >= grace;
  }

  // Reference `tick` so React's effect actually triggers re-renders.
  void tick;
  return false;
}
